import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, CheckCircle2, Sprout, ChevronRight, Layers } from 'lucide-react'
import { api } from '../lib/api'

const C = {
  bg: 'var(--color-bg)', bgRaised: 'var(--color-bg-card)', bgCard: '#1A2212',
  border: 'var(--color-border)', amber: 'var(--color-primary)', green: '#2D6A2F',
  text: 'var(--color-text)', textMuted: 'var(--color-text-muted)', textFaint: 'var(--color-text-faint)',
  red: '#F87171',
}

const STAGES = [
  { id: 'seeding',     label: 'Seeding',      color: '#A78BFA' },
  { id: 'germinated',  label: 'Germinated',   color: '#60A5FA' },
  { id: 'seedling',    label: 'Seedling',     color: '#34D399' },
  { id: 'rooted',      label: 'Rooted',       color: '#4ADE80' },
  { id: 'hardening',   label: 'Hardening',    color: '#F5A623' },
  { id: 'ready',       label: 'Ready',        color: '#F472B6' },
  { id: 'transplanted',label: 'Transplanted', color: 'var(--color-text-faint)' },
]

interface NurseryItem {
  id: string; species: string; variety_id?: string; propagation_method: string
  quantity: number; container_type: string; media: string; sow_date: string
  status: string; notes?: string
}

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: C.bg, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 5,
}

function AddBatchModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    species: '', variety_id: '', propagation_method: 'seed', quantity: '',
    container_type: 'cell_tray', media: 'potting_mix', sow_date: new Date().toISOString().slice(0, 10), notes: '',
  })
  const [success, setSuccess] = useState(false)
  const mutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => api.post('/nursery/items', d),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['nursery'] }); setSuccess(true); setTimeout(onClose, 1200) },
  })
  function submit(e: React.FormEvent) {
    e.preventDefault()
    mutation.mutate({ ...form, quantity: parseInt(form.quantity), status: 'seeding' })
  }
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: C.bgRaised, border: `1px solid ${C.border}`, borderRadius: 14, width: 480, maxWidth: '95vw', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>Add Nursery Batch</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textFaint }}><X size={18} /></button>
        </div>
        {success ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: '#4ADE80' }}><CheckCircle2 size={36} style={{ marginBottom: 10 }} /><div style={{ fontSize: 15, fontWeight: 600 }}>Batch added!</div></div>
        ) : (
          <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}><label style={labelStyle}>Species *</label><input required style={inputStyle} value={form.species} onChange={e => set('species', e.target.value)} placeholder="e.g. Mangifera indica" /></div>
              <div style={{ flex: 1 }}><label style={labelStyle}>Quantity *</label><input required type="number" min="1" style={inputStyle} value={form.quantity} onChange={e => set('quantity', e.target.value)} /></div>
            </div>
            <div>
              <label style={labelStyle}>Propagation Method</label>
              <select style={inputStyle} value={form.propagation_method} onChange={e => set('propagation_method', e.target.value)}>
                {['seed','cutting','graft','air-layer'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Container Type</label>
                <select style={inputStyle} value={form.container_type} onChange={e => set('container_type', e.target.value)}>
                  {['cell_tray','4in_pot','1gal','other'].map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Media</label>
                <select style={inputStyle} value={form.media} onChange={e => set('media', e.target.value)}>
                  {['potting_mix','coco','perlite','rockwool','other'].map(m => <option key={m} value={m}>{m.replace(/_/g,' ')}</option>)}
                </select>
              </div>
            </div>
            <div><label style={labelStyle}>Sow Date</label><input type="date" style={inputStyle} value={form.sow_date} onChange={e => set('sow_date', e.target.value)} /></div>
            <div><label style={labelStyle}>Notes</label><textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 64, fontFamily: 'inherit' }} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
            {mutation.isError && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: C.red + '11', borderRadius: 8 }}>Failed to save.</div>}
            <button type="submit" disabled={mutation.isPending} style={{ padding: '10px 0', borderRadius: 8, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              {mutation.isPending ? 'Saving…' : 'Add Batch'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

function NurseryCard({ item, onAdvance }: { item: NurseryItem; onAdvance: (id: string, status: string) => void }) {
  const stage = STAGES.find(s => s.id === item.status) ?? STAGES[0]
  const nextStage = STAGES[STAGES.findIndex(s => s.id === item.status) + 1]
  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>{item.species}</div>
      <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8 }}>
        {item.quantity} × {item.container_type.replace(/_/g,' ')} · {item.propagation_method}
      </div>
      <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 8 }}>
        Sown: {new Date(item.sow_date).toLocaleDateString([], { month: 'short', day: 'numeric' })}
        {' · '}{item.media.replace(/_/g,' ')}
      </div>
      {nextStage && (
        <button
          onClick={() => onAdvance(item.id, nextStage.id)}
          style={{ width: '100%', padding: '5px 0', borderRadius: 6, background: 'transparent', border: `1px solid ${stage.color}44`, color: stage.color, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
        >
          Advance → {nextStage.label} <ChevronRight size={11} />
        </button>
      )}
    </div>
  )
}

export function NurseryPage() {
  const [showAdd, setShowAdd] = useState(false)
  const qc = useQueryClient()

  const { data: items = [], isLoading, isError, refetch } = useQuery<NurseryItem[]>({
    queryKey: ['nursery'],
    queryFn: () => api.get('/nursery/items').then(r => r.data?.items ?? r.data ?? []),
  })

  const advanceMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/nursery/items/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['nursery'] }),
  })

  const itemsByStage = STAGES.reduce((acc, s) => {
    acc[s.id] = items.filter(i => i.status === s.id)
    return acc
  }, {} as Record<string, NurseryItem[]>)

  const totalQty = items.reduce((s, i) => s + i.quantity, 0)
  const readyCount = items.filter(i => i.status === 'ready').length
  const inProgress = items.filter(i => i.status !== 'transplanted' && i.status !== 'ready').length
  const speciesSet = new Set(items.map(i => i.species)).size

  return (
    <>
      {showAdd && <AddBatchModal onClose={() => setShowAdd(false)} />}
      <main id="main-content" style={{ padding: '28px 32px', minHeight: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>Nursery</h1>
            <p style={{ margin: '3px 0 0', fontSize: 13, color: C.textMuted }}>Seedling and propagation tracking</p>
          </div>
          <button onClick={() => setShowAdd(true)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 9, background: C.green, border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={16} /> Add Batch
          </button>
        </div>

        {/* Stat chips — only show when data exists */}
        {items.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Total batches', value: items.length, icon: Layers, color: C.amber },
              { label: 'Plants / Seeds', value: totalQty, icon: Sprout, color: '#4ADE80' },
              { label: 'In progress', value: inProgress, icon: Sprout, color: C.amber },
              { label: 'Ready to transplant', value: readyCount, icon: CheckCircle2, color: readyCount > 0 ? '#F472B6' : C.textFaint },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <Icon size={14} color={color} />
                  <span style={{ fontSize: 11, color: C.textMuted }}>{label}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Species summary chips */}
        {speciesSet > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
            {Array.from(new Set(items.map(i => i.species))).map(species => {
              const count = items.filter(i => i.species === species).length
              return (
                <span key={species} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 99, background: 'var(--color-bg-hover)', color: C.textMuted, border: `1px solid ${C.border}` }}>
                  {species} <span style={{ color: C.amber, fontWeight: 700 }}>{count}</span>
                </span>
              )
            })}
          </div>
        )}

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: C.textFaint }}>Loading nursery…</div>
        ) : isError ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <p style={{ color: C.red, marginBottom: 12 }}>Failed to load nursery items</p>
            <button onClick={() => refetch()} style={{ padding: '8px 16px', borderRadius: 8, background: C.bgCard, border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>Retry</button>
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Sprout size={40} style={{ color: C.textFaint, opacity: 0.4, marginBottom: 12 }} />
            <p style={{ margin: 0, color: C.textFaint }}>No nursery batches yet</p>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: C.textFaint }}>Click "Add Batch" to start tracking seedlings and propagations</p>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
            {STAGES.map(stage => (
              <div key={stage.id} style={{ flex: '0 0 200px', minWidth: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: stage.color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stage.label}</span>
                  <span style={{ fontSize: 11, color: C.textFaint, marginLeft: 'auto' }}>{itemsByStage[stage.id]?.length ?? 0}</span>
                </div>
                <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, minHeight: 120 }}>
                  {(itemsByStage[stage.id] ?? []).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px 0', color: C.textFaint, fontSize: 12 }}>Empty</div>
                  ) : (
                    (itemsByStage[stage.id] ?? []).map(item => (
                      <NurseryCard
                        key={item.id}
                        item={item}
                        onAdvance={(id, status) => advanceMutation.mutate({ id, status })}
                      />
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </>
  )
}
